'use client'

import * as React from 'react'
import { Heart, ArrowUpRight, X, BedDouble, Maximize2, Layers, MapPin } from 'lucide-react'
import { cn, proxyImageUrl } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { Chip, RealPhotos, ScrimBtn, fmtCZK, isLikelyMonthlyPrice, paletteFor, perM2, pillBtnBase } from '@/components/roostr'
import { useSaveListing } from './use-save-listing'
import { useListingAmenities } from '@/lib/use-listing-amenities'
import { kmToCentre } from './map-row-card'
import type { Listing } from '@/lib/types'
import type { MetricLens } from './map-metric-lens'

// Mini-card anchored to a map pin — design search.jsx:104-145, minus the
// AI-summary toggle (deferred). Sticky: opens on pin hover and stays until the
// X, an outside click, or a map move (owner: listing-map-google). Rendered as
// a *sibling* of the Google map container so the dark-mode tile inversion
// never touches it. Photos page inline via the shared RealPhotos chrome, so
// the price moved from a photo pill into the body (the pill's corner now
// belongs to the dot strip).

export interface MiniCardAnchor {
  /** Pin centre X, px within the map wrapper. */
  x: number
  /** Pin top edge, px within the map wrapper. */
  top: number
  /** Pin bottom edge, px within the map wrapper. */
  bottom: number
}

// 320, up from 252 (user 2026-08-16: "larger, more engaging, larger pictures,
// more info"). Wide enough for a 4:3 photo with real presence and a two-column
// stat band, still narrow enough to sit beside a pin without eating the map.
const CARD_W = 320
// Px from the L/R edge before the card pins to that edge, and px from the top
// edge before it flips below the pin — the prototype's 33%/67% and y<38%
// thresholds (search.jsx:106-110) translated to a real, resizable map.
// EDGE scales with the wider card so the flip still happens before the card
// would overhang.
const EDGE = 175
const FLIP_AT = 300

export interface MapMiniCardProps {
  listing: Listing
  lens: MetricLens
  anchor: MiniCardAnchor
  containerWidth: number
  containerHeight: number
  onOpen: () => void
  onClose: () => void
}

export function MapMiniCard({
  listing: l,
  lens,
  anchor,
  containerWidth,
  containerHeight,
  onOpen,
  onClose,
}: MapMiniCardProps) {
  const { t } = useI18n()
  const { isSaved, toggleSave } = useSaveListing(l.id)
  const imgs = (l.images ?? [])
    .map((im) => proxyImageUrl(im.url))
    .filter((u): u is string => !!u)

  // Vertical placement is CLAMPED to the map, not just flipped. The card grew
  // to ~480px; in a ~670px map neither "above the pin" nor "below the pin"
  // always fits, and the un-clamped version overflowed into the page header.
  // So: pick the side with more room, then clamp into the container and cap
  // the height (the body scrolls if a listing has unusually much content).
  const cardRef = React.useRef<HTMLDivElement>(null)
  const [measuredH, setMeasuredH] = React.useState(0)
  React.useLayoutEffect(() => {
    const h = cardRef.current?.getBoundingClientRect().height ?? 0
    if (h && Math.abs(h - measuredH) > 1) setMeasuredH(h)
  })

  // Placement rule, in priority order:
  //   1. Never overlap the pin. The card sitting on top of its own pin (user
  //      2026-08-16) made the pointer cross the card/pin boundary on the way
  //      to the card, re-firing pin enter/leave and re-popping the card in a
  //      loop.
  //   2. Above the pin when it fits, else below, else — when the card is
  //      taller than the room on either side — BESIDE it, so the pin stays
  //      clear and the card is still fully inside the map.
  //   3. Clamp inside the container. Earlier code clamped FIRST, which shoved
  //      a too-tall card back down over the pin — the exact bug.
  // Placement rule, in priority order:
  //   1. NEVER overlap the pin — structurally, not by arithmetic that depends
  //      on knowing the card's height. Placing "above" as `top = anchor.top -
  //      GAP - h` needs `h`, and on the first render `h` is only a GUESS
  //      (FLIP_AT); when the real card came out taller the card's bottom edge
  //      landed straight on the pin. So the ABOVE case pins the card's BOTTOM
  //      edge and lets it grow upward — the pin is then unreachable by
  //      construction, whatever the height turns out to be. Likewise BELOW
  //      pins the top edge and grows downward.
  //   2. Above when it fits, else below, else BESIDE (card taller than the
  //      room on either side), so the pin always stays clear.
  //   3. Bound the height to the room actually available on the chosen side,
  //      so it can never overflow the map instead. The body scrolls.
  const GAP = 14
  const MARGIN = 8
  // The visible pin is taller than the pill: `::after` draws the tip ~8px
  // below it, and the wrapper pads the hit area. Clear the tip, not the box.
  const PIN_TIP = 8
  const maxH = Math.max(180, containerHeight - MARGIN * 2)
  const h = Math.min(measuredH || FLIP_AT, maxH)
  const roomAbove = anchor.top - GAP - MARGIN
  const roomBelow = containerHeight - (anchor.bottom + PIN_TIP) - GAP - MARGIN

  type Placement = 'above' | 'below' | 'beside'
  let placement: Placement
  if (h <= roomAbove) placement = 'above'
  else if (h <= roomBelow) placement = 'below'
  else placement = 'beside'

  // Vertical: edge-anchored so the card grows AWAY from the pin.
  const vertical: React.CSSProperties = {}
  if (placement === 'above') {
    vertical.bottom = containerHeight - (anchor.top - GAP)
    vertical.maxHeight = Math.max(120, roomAbove)
  } else if (placement === 'below') {
    vertical.top = anchor.bottom + PIN_TIP + GAP
    vertical.maxHeight = Math.max(120, roomBelow)
  } else {
    vertical.top = Math.min(
      Math.max(MARGIN, anchor.top + (anchor.bottom - anchor.top) / 2 - h / 2),
      Math.max(MARGIN, containerHeight - h - MARGIN)
    )
    vertical.maxHeight = maxH
  }

  let left: number
  if (placement === 'beside') {
    // Right of the pin if there's room, else left; keep clear of the pin's
    // width (~40px) plus the gap.
    const pinHalf = 40
    if (anchor.x + pinHalf + GAP + CARD_W + MARGIN <= containerWidth) left = anchor.x + pinHalf + GAP
    else left = Math.max(MARGIN, anchor.x - pinHalf - GAP - CARD_W)
  } else if (anchor.x < EDGE) left = Math.max(8, anchor.x - 20)
  else if (anchor.x > containerWidth - EDGE)
    left = Math.min(containerWidth - CARD_W - 8, anchor.x - CARD_W + 20)
  else left = anchor.x - CARD_W / 2

  const style: React.CSSProperties = {
    position: 'absolute',
    width: CARD_W,
    zIndex: 40,
    left,
    ...vertical,
  }

  // Invisible hover BRIDGE between the card and its pin. The card is placed
  // clear of the pin (so it can't re-fire the pin's hover), which leaves a
  // strip of bare map between them; a hover-preview card hides shortly after
  // the pointer leaves the pin, so crossing that strip on the way to the card
  // hid it mid-journey and re-popped it on return (user 2026-08-16: "not
  // fixed — I HOVER, not click"). This element is a sibling of the card,
  // sized to cover exactly the gap, so the pointer is always over the card
  // wrapper — whose onMouseEnter cancels the hide.
  // It must NOT cover the pin itself (it renders above the map and would
  // steal the pin's hover), so it stops at the pin's outer edge, and it is
  // only pin-wide so it can't blanket neighbouring pins.
  // Computed from the card's KNOWN edge — the one we anchored — never from
  // top+height, which is exactly the estimate that used to be wrong.
  const PIN_HALF_W = 44 // widest pill ("130k") ≈ 80px + hit padding
  const pinTop = anchor.top - PIN_TIP
  const pinBottom = anchor.bottom + PIN_TIP
  const pinX = anchor.x
  let bridge: React.CSSProperties | null = null
  if (placement === 'above') {
    const cardBottomEdge = anchor.top - GAP
    bridge = {
      top: cardBottomEdge - 1,
      height: Math.max(0, pinTop - cardBottomEdge + 1),
      left: pinX - PIN_HALF_W,
      width: PIN_HALF_W * 2,
    }
  } else if (placement === 'below') {
    const cardTopEdge = anchor.bottom + PIN_TIP + GAP
    bridge = {
      top: pinBottom,
      height: Math.max(0, cardTopEdge - pinBottom + 1),
      left: pinX - PIN_HALF_W,
      width: PIN_HALF_W * 2,
    }
  } else if (left >= pinX + PIN_HALF_W) {
    bridge = { top: pinTop, height: pinBottom - pinTop, left: pinX + PIN_HALF_W, width: Math.max(0, left - (pinX + PIN_HALF_W) + 1) }
  } else if (left + CARD_W <= pinX - PIN_HALF_W) {
    bridge = { top: pinTop, height: pinBottom - pinTop, left: left + CARD_W - 1, width: Math.max(0, pinX - PIN_HALF_W - (left + CARD_W) + 1) }
  }

  const sub = lens.sub?.(l)

  // Stat band cells — the three numbers a renter checks first. Rendered as a
  // banded row (same idiom as the grid card) rather than a "2+kk · 48 m² ·
  // Praha 5" run-on, which was hard to parse at a glance.
  const stats = [
    typeof l.rooms === 'number' ? { icon: BedDouble, v: `${l.rooms}+kk` } : null,
    typeof l.area === 'number' ? { icon: Maximize2, v: `${l.area} m²` } : null,
    typeof l.floor === 'number' ? { icon: Layers, v: `${l.floor}.` } : null,
  ].filter(Boolean) as { icon: typeof BedDouble; v: string }[]

  // Price per m² — the comparison number, and cheap to derive.
  const sqm =
    l.price > 0 && typeof l.area === 'number' && l.area > 0
      ? perM2({ price: l.price, area: l.area })
      : null

  const amenities = useListingAmenities(l)
  const km =
    l.location?.lat && l.location?.lon ? kmToCentre(l.location.lat, l.location.lon) : null

  return (
    <>
      {bridge && (
        <div
          aria-hidden
          style={{ position: 'absolute', zIndex: 39, ...bridge }}
          // pointer-events auto on purpose: this is the hover bridge.
        />
      )}
    <div ref={cardRef} style={style} className="animate-pop-in overflow-y-auto">
      <div className="overflow-hidden rounded-shape-md border border-outline-variant bg-surface shadow-e3">
        <RealPhotos
          images={imgs}
          palette={paletteFor(l.id)}
          // 4:3, not 16:9 — at 320 wide that's a 240px photo instead of 180,
          // which is what makes the card read as a listing rather than a
          // tooltip.
          aspect="4 / 3"
          arrowsAlwaysVisible
          overlay={
            <>
              <span className="absolute left-2 top-2">
                <ScrimBtn icon={X} size={30} title={t('search.close', 'Close')} onClick={() => onClose()} />
              </span>
              <span className="absolute right-2 top-2">
                <ScrimBtn
                  icon={Heart}
                  size={30}
                  active={isSaved}
                  title={isSaved ? t('search.unlike', 'Remove from liked') : t('search.like', 'Like')}
                  onClick={(e) => toggleSave(e)}
                />
              </span>
            </>
          }
        />
        <div className="px-3.5 pb-3 pt-2.5">
          {/* Price line: the number, and next to it the per-m² comparison. */}
          <div className="flex items-baseline gap-1.5">
            <span className={cn('font-mono text-[19px] font-bold', l.price > 0 ? 'text-primary' : 'text-ink-muted')}>
              {l.price > 0 ? fmtCZK(l.price) : t('listingCard.noPrice', 'No price')}
            </span>
            {l.price > 0 && isLikelyMonthlyPrice(l.price) && (
              <span className="font-mono text-[11.5px] text-ink-muted">{t('listingCard.perMo', '/mo')}</span>
            )}
            {sqm != null && (
              <span className="ml-auto whitespace-nowrap font-mono text-[11px] text-ink-muted">
                {fmtCZK(sqm)}/m²
              </span>
            )}
          </div>

          <div className="mt-[3px] line-clamp-2 text-[13.5px] font-bold leading-[1.3]">{l.title}</div>

          {/* Location line — district plus straight-line distance to the
              centre, the two things that place a flat in your head. */}
          {(l.district || km != null) && (
            <div className="mt-[3px] flex items-center gap-1 text-[11.5px] text-ink-secondary">
              <MapPin className="h-3 w-3 shrink-0 text-ink-muted" aria-hidden />
              <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                {[l.district, l.city].filter(Boolean).join(', ')}
              </span>
              {km != null && (
                <span className="ml-auto whitespace-nowrap font-mono text-[10.5px] text-ink-muted">
                  {km.toFixed(1)} {t('search.km', 'km')} → {t('search.centre', 'centre')}
                </span>
              )}
            </div>
          )}

          {stats.length > 0 && (
            <div className="mt-2 flex items-center rounded-shape-sm bg-surface-low py-1.5">
              {stats.map(({ icon: Icon, v }, i) => (
                <div
                  key={v}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 text-[12.5px] font-bold',
                    i > 0 && 'border-l border-outline-variant'
                  )}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0 text-ink-muted" aria-hidden />
                  {v}
                </div>
              ))}
            </div>
          )}

          {/* A few features — enough to judge, not a wall. */}
          {amenities.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {amenities.slice(0, 4).map(({ key, label, icon: Icon }) => (
                <Chip key={key} small label={label} icon={<Icon size={11} className="shrink-0" />} />
              ))}
              {amenities.length > 4 && (
                <span className="self-center text-[11px] text-ink-muted">+{amenities.length - 4}</span>
              )}
            </div>
          )}

          {sub && <div className="mt-2 text-[11.5px] text-ink-secondary">{sub}</div>}

          <button
            type="button"
            onClick={onOpen}
            className={cn(
              pillBtnBase,
              'mt-2.5 flex w-full justify-center bg-primary-container px-3 py-2 text-[12.5px] font-semibold text-on-primary-container'
            )}
          >
            {t('search.details', 'Details')}
            <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </div>
    </>
  )
}
